import { createSlice } from '@reduxjs/toolkit';

const STORAGE_PREFIX = 'mps_';

export const MPS_STORAGE_KEYS = {
  companyId: `${STORAGE_PREFIX}companyId`,
  filters: `${STORAGE_PREFIX}filters`,
  selectedProjects: `${STORAGE_PREFIX}selectedProjects`,
  selectedSprintIds: `${STORAGE_PREFIX}selectedSprintIds`,
  selectedReleaseIds: `${STORAGE_PREFIX}selectedReleaseIds`,
  selectedRepos: `${STORAGE_PREFIX}selectedRepos`,
  filterMode: `${STORAGE_PREFIX}filterMode`,
  selectedDeveloper: `${STORAGE_PREFIX}selectedDeveloper`,
};

function readJson(key, fallback) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  if (value === null || value === undefined) {
    sessionStorage.removeItem(key);
    return;
  }
  sessionStorage.setItem(key, JSON.stringify(value));
}

function readString(key) {
  const value = sessionStorage.getItem(key);
  return value || null;
}

function writeString(key, value) {
  if (value === null || value === undefined || value === '') {
    sessionStorage.removeItem(key);
    return;
  }
  sessionStorage.setItem(key, value);
}

function persistMultiProjectState(state) {
  writeString(MPS_STORAGE_KEYS.companyId, state.companyId);
  writeJson(MPS_STORAGE_KEYS.filters, state.filters);
  writeJson(MPS_STORAGE_KEYS.selectedProjects, state.selectedProjects);
  writeJson(MPS_STORAGE_KEYS.selectedSprintIds, state.selectedSprintIds);
  writeJson(MPS_STORAGE_KEYS.selectedReleaseIds, state.selectedReleaseIds);
  writeJson(MPS_STORAGE_KEYS.selectedRepos, state.selectedRepos);
  writeString(MPS_STORAGE_KEYS.filterMode, state.filterMode);
  writeString(MPS_STORAGE_KEYS.selectedDeveloper, state.selectedDeveloper);
}

function clearMultiProjectSessionStorage() {
  Object.values(MPS_STORAGE_KEYS).forEach((key) => sessionStorage.removeItem(key));
}

function loadInitialState() {
  return {
    companyId: readString(MPS_STORAGE_KEYS.companyId),
    filters: readJson(MPS_STORAGE_KEYS.filters, null),
    selectedProjects: readJson(MPS_STORAGE_KEYS.selectedProjects, []),
    selectedSprintIds: readJson(MPS_STORAGE_KEYS.selectedSprintIds, []),
    selectedReleaseIds: readJson(MPS_STORAGE_KEYS.selectedReleaseIds, []),
    selectedRepos: readJson(MPS_STORAGE_KEYS.selectedRepos, []),
    filterMode: readString(MPS_STORAGE_KEYS.filterMode),
    selectedDeveloper: readString(MPS_STORAGE_KEYS.selectedDeveloper),
    issuesData: null,
    loading: false,
    error: null,
  };
}

const initialState = loadInitialState();

const multiProjectSlice = createSlice({
  name: 'multiProject',
  initialState,
  reducers: {
    setMultiProjectCompanyId(state, action) {
      state.companyId = action.payload ?? null;
      persistMultiProjectState(state);
    },
    setMultiProjectFilters(state, action) {
      state.filters = action.payload ?? null;
      persistMultiProjectState(state);
    },
    setMultiProjectSelections(state, action) {
      const {
        selectedProjects,
        selectedSprintIds,
        selectedReleaseIds,
        selectedRepos,
        filterMode,
        selectedDeveloper,
      } = action.payload || {};

      if (selectedProjects !== undefined) {
        state.selectedProjects = selectedProjects;
      }
      if (selectedSprintIds !== undefined) {
        state.selectedSprintIds = selectedSprintIds;
      }
      if (selectedReleaseIds !== undefined) {
        state.selectedReleaseIds = selectedReleaseIds;
      }
      if (selectedRepos !== undefined) {
        state.selectedRepos = selectedRepos;
      }
      if (filterMode !== undefined) {
        state.filterMode = filterMode;
      }
      if (selectedDeveloper !== undefined) {
        state.selectedDeveloper = selectedDeveloper;
      }
      persistMultiProjectState(state);
    },
    setSelectedProjects(state, action) {
      state.selectedProjects = action.payload ?? [];
      persistMultiProjectState(state);
    },
    setSelectedSprintIds(state, action) {
      state.selectedSprintIds = action.payload ?? [];
      persistMultiProjectState(state);
    },
    setSelectedReleaseIds(state, action) {
      state.selectedReleaseIds = action.payload ?? [];
      persistMultiProjectState(state);
    },
    setSelectedRepos(state, action) {
      state.selectedRepos = action.payload ?? [];
      persistMultiProjectState(state);
    },
    setMultiProjectFilterMode(state, action) {
      state.filterMode = action.payload ?? null;
      persistMultiProjectState(state);
    },
    setMultiProjectDeveloper(state, action) {
      state.selectedDeveloper = action.payload ?? null;
      persistMultiProjectState(state);
    },
    setMultiProjectIssuesData(state, action) {
      state.issuesData = action.payload ?? null;
    },
    setMultiProjectLoading(state, action) {
      state.loading = Boolean(action.payload);
    },
    setMultiProjectError(state, action) {
      state.error = action.payload ?? null;
    },
    resetMultiProjectState() {
      clearMultiProjectSessionStorage();
      return {
        ...loadInitialState(),
        filters: null,
        selectedProjects: [],
        selectedSprintIds: [],
        selectedReleaseIds: [],
        selectedRepos: [],
        filterMode: null,
        selectedDeveloper: null,
      };
    },
    hydrateMultiProjectFromSession(state) {
      const hydrated = loadInitialState();
      state.companyId = hydrated.companyId;
      state.filters = hydrated.filters;
      state.selectedProjects = hydrated.selectedProjects;
      state.selectedSprintIds = hydrated.selectedSprintIds;
      state.selectedReleaseIds = hydrated.selectedReleaseIds;
      state.selectedRepos = hydrated.selectedRepos;
      state.filterMode = hydrated.filterMode;
      state.selectedDeveloper = hydrated.selectedDeveloper;
    },
  },
});

export const {
  setMultiProjectCompanyId,
  setMultiProjectFilters,
  setMultiProjectSelections,
  setSelectedProjects,
  setSelectedSprintIds,
  setSelectedReleaseIds,
  setSelectedRepos,
  setMultiProjectFilterMode,
  setMultiProjectDeveloper,
  setMultiProjectIssuesData,
  setMultiProjectLoading,
  setMultiProjectError,
  resetMultiProjectState,
  hydrateMultiProjectFromSession,
} = multiProjectSlice.actions;

export default multiProjectSlice.reducer;
